import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';

interface AnnotateScreenshotModalProps {
  open: boolean;
  /** The raw captured screenshot as a data URL. */
  screenshotDataUrl: string;
  /** Called when the user clicks "Next" with the annotated canvas data URL. */
  onNext: (annotatedDataUrl: string) => void;
  /** Called when the user clicks "Cancel". */
  onCancel: () => void;
}

interface Point {
  x: number;
  y: number;
}

interface TextElement {
  text: string;
  x: number;
  y: number;
  lines: string[];
  color: string;
}

const STROKE_COLOR = 'red';
const STROKE_WIDTH = 3;
const TEXT_FONT = '24px Arial';
const TEXT_FONT_SMALL = '18px Arial';
const TEXT_LINE_HEIGHT = 22;

/*
  The annotation modal pierces the shared <Modal> wrapper to (a) widen the
  panel to 80vw (the canvas needs the full viewport), (b) hide the default
  modal header (we render a custom heading banner), and (c) recolour the
  panel with the dark-theme tokens used by SubmitTicketModal. The override
  block below is the minimal CSS we can't express as utility classes on the
  React tree because Modal.tsx renders the .rw-modal__panel itself.
*/
const ANNOTATE_MODAL_STYLE = `
.annotate-modal .rw-modal__panel {
  max-width: 80vw;
  width: 80vw;
  background: #1a1a1a;
  color: #ffffff;
  border: 1px solid #2a2a2a;
  border-radius: 10px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.annotate-modal .rw-modal__header { display: none; }
.annotate-modal .rw-modal__body {
  padding: 0 0 14px;
  display: flex;
  flex-direction: column;
}
.annotate-modal .rw-modal__footer {
  border-top: 1px solid #2a2a2a;
  padding: 14px 22px;
  gap: 12px;
  justify-content: center;
}
`;

// ── Tailwind class bundles ────────────────────────────────────────────────
const HEADING_CLASS =
  'font-hk-bold text-base leading-[26px] text-center w-full py-2 px-[14px] text-white';

const NOTE_CLASS =
  'bg-[#262626] flex justify-center text-center leading-10 min-h-10 py-1 px-[14px] text-[13px] text-white/[0.62]';

// Canvas container — flex column, fixed padding. The container is the
// positioning ancestor for the inline <textarea> the canvas creates on
// double-click (see onDblClick in the effect below).
const CANVAS_CONTAINER_CLASS =
  'flex flex-col items-center justify-center p-[10px] max-w-full max-h-[80vh] relative' +
  ' [&_canvas]:cursor-crosshair [&_canvas]:w-full [&_canvas]:h-[60vh] [&_canvas]:block';

// Footer buttons — replicates `.ticket-form__btn--{secondary,primary}` for
// the annotate modal so we don't depend on SubmitTicketModal styles.
const BTN_BASE =
  'h-9 px-[22px] rounded-md text-[13px] font-hk-semibold tracking-[0.2px] cursor-pointer' +
  ' border border-solid border-transparent' +
  ' transition-[background-color,color,border-color,box-shadow] duration-[150ms] ease-linear' +
  ' disabled:opacity-55 disabled:cursor-not-allowed';
const BTN_SECONDARY =
  ' bg-transparent border-[#373737] text-white' +
  ' enabled:hover:bg-white/[0.06] enabled:hover:border-white/[0.25]';
const BTN_PRIMARY =
  ' bg-[#0eaa6b] border-[#0eaa6b] text-white' +
  ' enabled:hover:bg-[#0c8d59] enabled:hover:border-[#0c8d59]';

const AnnotateScreenshotModal: React.FC<AnnotateScreenshotModalProps> = ({
  open,
  screenshotDataUrl,
  onNext,
  onCancel,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Mutable drawing state -- refs because they do NOT drive React re-render.
  const drawingRef = useRef(false);
  const pathsRef = useRef<Point[][]>([]);
  const currentPathRef = useRef<Point[]>([]);
  const textElementsRef = useRef<TextElement[]>([]);
  const selectedTextRef = useRef<TextElement | null>(null);
  const isDraggingTextRef = useRef(false);
  const offsetRef = useRef<Point>({ x: 0, y: 0 });

  // ── Helper: draw a single stroke path ──
  const drawPath = useCallback((ctx: CanvasRenderingContext2D, path: Point[]): void => {
    if (path.length < 2) return;
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = STROKE_WIDTH;
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    path.forEach((point) => ctx.lineTo(point.x, point.y));
    ctx.stroke();
  }, []);

  // ── Helper: redraw everything ──
  const drawAll = useCallback((): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imgRef.current;
    if (!canvas || !ctx || !img) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    // Redraw saved strokes
    pathsRef.current.forEach((p) => drawPath(ctx, p));

    // Redraw text elements
    textElementsRef.current.forEach(({ x, y, lines, color }) => {
      ctx.fillStyle = color || 'black';
      ctx.font = TEXT_FONT;
      lines.forEach((line, index) => {
        ctx.fillText(line, x, y + index * TEXT_LINE_HEIGHT);
      });
    });
  }, [drawPath]);

  // ── Helper: convert client mouse coords to canvas coords ──
  const getMousePos = useCallback((e: MouseEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // ── Helper: hit-test a single text element ──
  const checkTextBounds = useCallback(
    (
      mouseX: number,
      mouseY: number,
      textItem: TextElement,
      ctx: CanvasRenderingContext2D,
      scaleX: number,
      scaleY: number,
    ): boolean => {
      ctx.font = TEXT_FONT_SMALL;
      const lineHeight = TEXT_LINE_HEIGHT * scaleY;
      const textHeight = textItem.lines.length * lineHeight;
      const textWidths = textItem.lines.map((line) => ctx.measureText(line).width * scaleX);
      const maxTextWidth = Math.max(...textWidths);

      return (
        mouseX >= textItem.x &&
        mouseX <= textItem.x + maxTextWidth &&
        mouseY >= textItem.y - textHeight &&
        mouseY <= textItem.y
      );
    },
    [],
  );

  // ── Helper: is the click on any text element? ──
  const isClickingText = useCallback(
    (e: MouseEvent, textItem?: TextElement): boolean => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return false;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;

      if (textItem) {
        return checkTextBounds(mouseX, mouseY, textItem, ctx, scaleX, scaleY);
      }
      return textElementsRef.current.some((te) =>
        checkTextBounds(mouseX, mouseY, te, ctx, scaleX, scaleY),
      );
    },
    [checkTextBounds],
  );

  // ── Set up canvas and event listeners when modal opens ──
  useEffect(() => {
    if (!open) return;

    // Reset mutable state for a fresh annotation session.
    drawingRef.current = false;
    pathsRef.current = [];
    currentPathRef.current = [];
    textElementsRef.current = [];
    selectedTextRef.current = null;
    isDraggingTextRef.current = false;
    offsetRef.current = { x: 0, y: 0 };

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    imgRef.current = img;
    img.src = screenshotDataUrl;

    img.onload = (): void => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      canvas.width = imgWidth;
      canvas.height = imgHeight;
      ctx.drawImage(img, 0, 0, imgWidth, imgHeight);

      // Maintain aspect ratio for CSS display size (mirrors Vue logic lines 326-348)
      const aspectRatio = imgWidth / imgHeight;
      const maxHeight = window.innerHeight * 0.7;
      const maxWidth = window.innerWidth;

      let displayWidth: number;
      let displayHeight: number;

      if (imgWidth > maxWidth || imgHeight > maxHeight) {
        if (maxWidth / aspectRatio <= maxHeight) {
          displayWidth = maxWidth;
          displayHeight = maxWidth / aspectRatio;
        } else {
          displayHeight = maxHeight;
          displayWidth = maxHeight * aspectRatio;
        }
      } else {
        displayWidth = imgWidth;
        displayHeight = imgHeight;
      }

      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;
    };

    // ── mousedown ──
    const onMouseDown = (e: MouseEvent): void => {
      const { x, y } = getMousePos(e);
      const clicked = textElementsRef.current.find((te) => isClickingText(e, te)) ?? null;
      if (clicked) {
        selectedTextRef.current = clicked;
        isDraggingTextRef.current = true;
        offsetRef.current = { x: x - clicked.x, y: y - clicked.y };
      } else {
        drawingRef.current = true;
        currentPathRef.current = [{ x, y }];
      }
    };

    // ── mousemove ──
    const onMouseMove = (e: MouseEvent): void => {
      const { x, y } = getMousePos(e);

      if (isDraggingTextRef.current && selectedTextRef.current) {
        const localCtx = canvas.getContext('2d');
        if (!localCtx) return;
        localCtx.font = TEXT_FONT_SMALL;

        let newX = x - offsetRef.current.x;
        let newY = y - offsetRef.current.y;

        const availableWidth = canvas.width - newX - 10;
        const words = selectedTextRef.current.text.split(' ');
        let line = '';
        const lines: string[] = [];

        for (const word of words) {
          const testLine = line + word + ' ';
          const testWidth = localCtx.measureText(testLine).width;
          if (testWidth > availableWidth) {
            lines.push(line);
            line = word + ' ';
          } else {
            line = testLine;
          }
        }
        lines.push(line);

        const textWidth = Math.max(...lines.map((l) => localCtx.measureText(l).width));
        const textHeight = lines.length * TEXT_LINE_HEIGHT;

        newX = Math.max(0, Math.min(canvas.width - textWidth, newX));
        newY = Math.max(TEXT_LINE_HEIGHT, Math.min(canvas.height - textHeight, newY));

        selectedTextRef.current.x = newX;
        selectedTextRef.current.y = newY;
        selectedTextRef.current.lines = lines;

        drawAll();
      } else if (drawingRef.current) {
        currentPathRef.current.push({ x, y });
        drawAll();
        drawPath(ctx, currentPathRef.current);
      }
    };

    // ── mouseup ──
    const onMouseUp = (): void => {
      if (drawingRef.current) {
        pathsRef.current.push([...currentPathRef.current]);
        currentPathRef.current = [];
        drawingRef.current = false;
      }
      isDraggingTextRef.current = false;
    };

    // ── dblclick -- create inline textarea for text annotation ──
    const onDblClick = (e: MouseEvent): void => {
      const container = containerRef.current;
      if (!container) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;

      const input = document.createElement('textarea');
      input.style.position = 'absolute';
      // Position relative to the container, offset from canvas top-left
      input.style.left = `${e.clientX - rect.left}px`;
      input.style.top = `${e.clientY - container.getBoundingClientRect().top}px`;
      input.style.border = 'none';
      input.style.background = 'transparent';
      input.style.fontSize = '18px';
      input.style.fontFamily = 'Arial, sans-serif';
      input.style.outline = 'none';
      input.style.color = 'red';
      input.style.resize = 'none';
      input.style.overflow = 'hidden';
      input.style.overflowWrap = 'break-word';
      input.style.whiteSpace = 'pre-wrap';
      input.style.zIndex = '100000';

      const maxWidth = (canvas.width - canvasX) / scaleX - 10;
      const maxHeight = (canvas.height - canvasY) / scaleY - 10;
      input.style.maxWidth = `${maxWidth}px`;
      input.style.maxHeight = `${maxHeight}px`;

      container.appendChild(input);
      input.focus();

      const onInput = (): void => {
        const localCtx = canvas.getContext('2d');
        if (!localCtx) return;
        localCtx.font = TEXT_FONT_SMALL;
        const textWidth = localCtx.measureText(input.value).width * scaleX;
        if (textWidth + 20 > maxWidth) {
          input.style.width = `${maxWidth}px`;
          input.style.height = 'auto';
        } else {
          input.style.width = `${Math.max(50, textWidth + 20)}px`;
        }
      };

      const onBlur = (): void => {
        if (input.value.trim()) {
          const localCtx = canvas.getContext('2d');
          if (localCtx) {
            localCtx.font = TEXT_FONT_SMALL;
            localCtx.fillStyle = 'red';

            const words = input.value.split(' ');
            let line = '';
            const lines: string[] = [];
            const availableWidth = (canvas.width - canvasX) * scaleX;

            for (const word of words) {
              const testLine = line + word + ' ';
              const testWidth = localCtx.measureText(testLine).width * scaleX;
              if (testWidth > availableWidth) {
                lines.push(line);
                line = word + ' ';
              } else {
                line = testLine;
              }
            }
            lines.push(line);

            const textHeight = lines.length * TEXT_LINE_HEIGHT;
            const clampedY = Math.min(canvasY, canvas.height - textHeight);

            textElementsRef.current.push({
              text: input.value,
              x: canvasX,
              y: clampedY,
              lines,
              color: 'red',
            });
            drawAll();
          }
        }
        input.removeEventListener('input', onInput);
        input.removeEventListener('blur', onBlur);
        if (container.contains(input)) {
          container.removeChild(input);
        }
      };

      input.addEventListener('input', onInput);
      input.addEventListener('blur', onBlur);
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('dblclick', onDblClick);

    // Cleanup listeners when modal closes or component unmounts
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('dblclick', onDblClick);
    };
  }, [open, screenshotDataUrl, getMousePos, isClickingText, drawAll, drawPath]);

  // ── "Next" handler: capture annotated canvas as data URL ──
  const handleNext = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const annotatedDataUrl = canvas.toDataURL('image/png');
    onNext(annotatedDataUrl);
  }, [onNext]);

  return (
    <>
      <style>{ANNOTATE_MODAL_STYLE}</style>
      <Modal
        open={open}
        onClose={onCancel}
        title={t('reportBug.annotationHeading')}
        size="lg"
        className="annotate-modal"
        maskClosable={false}
        footer={
          <>
            <button
              type="button"
              className={BTN_BASE + BTN_SECONDARY}
              onClick={onCancel}
            >
              {t('reportBug.cancel')}
            </button>
            <button
              type="button"
              className={BTN_BASE + BTN_PRIMARY}
              onClick={handleNext}
            >
              {t('reportBug.next')}
            </button>
          </>
        }
      >
        <div className={HEADING_CLASS}>{t('reportBug.annotationHeading')}</div>
        <div className={NOTE_CLASS}>{t('reportBug.editNote')}</div>
        <div className={CANVAS_CONTAINER_CLASS} ref={containerRef}>
          <canvas ref={canvasRef} />
        </div>
      </Modal>
    </>
  );
};

export default AnnotateScreenshotModal;
